import { LoadingSpinner, IconButton, Text } from "@oysterhr/ui"

import { useEffect, MutableRefObject, useState } from "react"
import { useTranslation } from "react-i18next"
import { SalaryCalculation } from "@oysterhr/schema"
import classNames from "classnames"
import { QuoteCycle } from "../../types"
import { getAmountWithCommas } from "../../utils"
import { useCalculationsParams, useTotalHiringCost } from "../../hooks"
import { convertSubunitIntoUnit, convertUnitIntoSubunit } from "@oysterhr/common"
import { CurrencySelect } from "@oysterhr/feature-components"

interface TotalHiringCostProps {
  calculations: Array<SalaryCalculation>
  cycle: QuoteCycle
  toggleMobileList?: () => void
  isMobileListExpanded?: boolean
  containerRef?: MutableRefObject<HTMLDivElement | null>
  companyCurrencyCode?: string
}

export function TotalHiringCost({
  calculations,
  cycle,
  toggleMobileList,
  isMobileListExpanded,
  containerRef,
  companyCurrencyCode,
}: TotalHiringCostProps) {
  const { t } = useTranslation("CostCalculator")
  const { getParam, setTotalCurrencyParam } = useCalculationsParams()

  /**
   * The currency that should be used by the total cost form
   * The order of priority should be the following:
   * 1. The currency that is in the URL (This is because it was selected by the user)
   * 2. The default company currency code
   * 3. Defaults to EUR
   */

  const totalCurrencyParam = getParam("totalCurrency")
  const totalCurrency = totalCurrencyParam
    ? totalCurrencyParam
    : companyCurrencyCode
      ? companyCurrencyCode
      : "EUR"

  const [selectedCurrency, setSelectedCurrency] = useState<string>(totalCurrency)

  const [getTotalHiringCosts, { data, loading }] = useTotalHiringCost()

  const totalAmount =
    data?.totalMultipleCurrencies &&
    convertSubunitIntoUnit(
      data?.totalMultipleCurrencies.subunit,
      data?.totalMultipleCurrencies?.currencyCode,
    )

  const totalHiringCosts = totalAmount ? getAmountWithCommas(totalAmount, cycle) : "-"

  const onCurrencySelection = (currency: string | undefined | null) => {
    if (currency) {
      setTotalCurrencyParam(currency)
      setSelectedCurrency(currency)
    }
  }

  useEffect(() => {
    const currencies = calculations.map(calculation => {
      const currencyCode = calculation.currency.code
      const subunit = convertUnitIntoSubunit(calculation.totals.employerCosts, currencyCode)

      return { currencyCode, subunit }
    })

    getTotalHiringCosts({
      variables: {
        currencyCode: selectedCurrency,
        currencies,
      },
    })
  }, [selectedCurrency, getTotalHiringCosts, calculations])

  return (
    <div
      className={
        "sticky bottom-0 space-y-1 border-t border-gray-200 bg-gray-100 px-4 py-3 pt-4 text-left"
      }
      ref={containerRef}
    >
      <div className="relative -ml-4 w-full bg-gray-100">
        {/* Styles for this are temp fix to make it visible until design team provides a better suggestion */}
        <IconButton
          aria-label={t("toggleMenu")}
          iconName={isMobileListExpanded ? "ChevronDown" : "ChevronUp"}
          variation="outline"
          className={classNames(
            "absolute -top-10 left-[50%] rounded-full border-gray-200 transition duration-200 ease-in-out hover:drop-shadow-lg lg:hidden",
          )}
          onClick={toggleMobileList}
          style={{ backgroundColor: "inherit" }}
          size="md"
        />
      </div>
      <div className="flex flex-col justify-between space-x-0 space-y-2 font-bold md:flex-row md:items-center md:space-x-2 md:space-y-0">
        <Text size="small" className="font-semibold">
          {t("totalHiringCosts")}
        </Text>
        <div className="flex flex-row items-center md:space-x-2">
          <div className="border-back hidden w-fit rounded-md border bg-white pl-1.5 text-sm lg:block">
            <CurrencySelect value={selectedCurrency} onChange={onCurrencySelection} />
          </div>
          <div className="border-back w-fit rounded-md border bg-white pl-1.5 text-sm lg:hidden">
            <CurrencySelect value={selectedCurrency} onChange={onCurrencySelection} />
          </div>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <Text size="small" className="ml-2 font-semibold">
              {totalHiringCosts}
            </Text>
          )}
        </div>
      </div>
    </div>
  )
}
